import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { isAxiosError } from 'axios';
import {
  Box,
  Select,
  Button,
  Checkbox,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { CONSTANT_KEYS } from '../../helpers/tableKeys';
import { languageOption, CODING_GROUP } from '../../helpers/options';
import { codeList } from '../../utils/filters';
import { getCodeList } from '../../services';

const { CODE, NAME_RU, NAME_EN, PROFILE } = CONSTANT_KEYS;
const { CODE__EXACT, CODING_GROUP__EXACT } = codeList;

const initialCodeData = {
  [CODE]: '',
  [NAME_RU]: '',
  [NAME_EN]: '',
  codeId: null,
};

const AddICDCodeForm = ({ onSubmit, onCancel, languages, profiles }) => {
  const [data, setData] = useState({
    [PROFILE]: [],
    ...initialCodeData,
  });
  const [error, setError] = useState({});
  const [codeLoading, setCodeLoading] = useState(false);
  const [isCodeExists, setIsCodeExists] = useState(false);

  const debounceTimeout = useRef(null);
  const { t } = useTranslation(['diagnoses', 'promptbar']);

  useEffect(() => {
    setData(prev => ({
      ...prev,
      [PROFILE]: profiles
        .filter(
          profile =>
            profile.group === CODING_GROUP.GENERAL ||
            profile.group === CODING_GROUP.DIAGNOSES,
        )
        .map(p => p.profile_name),
    }));
  }, [profiles]);

  const codeSchema = Yup.string().trim().required(t('promptbar:requiredField'));

  const schema = Yup.object().shape({
    [CODE]: Yup.string().trim().required(t('promptbar:requiredField')),
    [NAME_RU]: Yup.string()
      .matches(/[a-zA-Zа-яА-Я]+/, t('promptbar:incorrectFormat'))
      .required(t('promptbar:requiredField')),
    [NAME_EN]: Yup.string().matches(
      /[a-zA-Z]*/,
      t('promptbar:incorrectFormat'),
    ),
  });

  const handleChange = event => {
    const { value, name } = event.target;

    removeError(name);

    setData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangeCase = e => {
    const { value, name } = e.target;
    const newValue = value.charAt(0).toUpperCase() + value.slice(1);

    setData(prev => ({ ...prev, [name]: newValue }));
  };

  // введение кода и получение наименований
  const handleCodeChange = event => {
    const { value, name } = event.target;
    const formattedValue = value.trim();
    setData(prev => ({ ...prev, ...initialCodeData, [name]: value }));
    setIsCodeExists(false);
    setError(prev => ({
      ...prev,
      [name]: '',
      [NAME_RU]: '',
      [NAME_EN]: '',
    }));

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        await codeSchema.validate(formattedValue, { abortEarly: false });

        setCodeLoading(true);

        const response = await getCodeList({
          [CODE__EXACT]: formattedValue,
          [CODING_GROUP__EXACT]: CODING_GROUP.DIAGNOSES,
        });

        if (response) {
          const basicProfileCode = response.data.find(el =>
            el.profiles.some(item => item.technical),
          );

          if (basicProfileCode) {
            const newValues = {};
            basicProfileCode?.names?.forEach(({ language_name, name }) => {
              const key = `name_${language_name.toLowerCase()}`;
              newValues[key] = name;
            });

            setIsCodeExists(true);
            setData(prev => ({
              ...prev,
              ...newValues,
              codeId: basicProfileCode.code_id,
            }));
          }
        }
      } catch (error) {
        if (error.name === 'ValidationError') {
          console.error('Ошибка валидации:', error.message);
          const validationError = {};

          error.inner.forEach(error => {
            validationError[name] = error.message;
          });
          setError(prev => ({ ...prev, ...validationError }));
        } else if (isAxiosError(error)) {
          console.error(
            'Ошибка запроса:',
            error.response?.data || error.message,
          );
        } else {
          console.error('Неизвестная ошибка:', error.message);
        }
      } finally {
        setCodeLoading(false);
      }
    }, 500);
  };

  const handleFormSubmit = e => {
    e.preventDefault();

    const newProfiles = profiles
      .filter(profile => data[PROFILE].includes(profile.profile_name))
      .map(pr => ({
        ...pr,
        name: pr.profile_name,
        profile_name: undefined,
      }));

    const newData = {
      code: data[CODE].trim(),
      age: '',
      comment: '',
      teeth: '',
      coding: null,
      names: languages.map(lang => ({
        language_id: lang.id,
        name:
          lang.name === languageOption[0]
            ? data[NAME_RU].trim()
            : lang.name === languageOption[1]
              ? data[NAME_EN].trim()
              : '',
      })),
      coding_group: CODING_GROUP.DIAGNOSES,
      profiles: newProfiles,
    };

    onSubmit(newData, data?.codeId);
  };

  const removeError = name => {
    if (!error[name]) {
      return;
    }
    const tmpError = { ...error };
    delete tmpError[name];
    setError(tmpError);
  };

  const handleValidate = async data => {
    try {
      await schema.validate(data, { abortEarly: false });
    } catch (errors) {
      const validationError = {};

      errors.inner.forEach(error => {
        validationError[error.path] = error.message;
      });
      setError(prev => ({ ...prev, ...validationError }));
    }
  };

  const isDisabledSaveBtn =
    !data[CODE].trim() ||
    !data[NAME_RU].trim() ||
    !data[NAME_EN].trim() ||
    !data[PROFILE].length;

  return (
    <Box component="form" autoComplete="off">
      <FormControl fullWidth variant="standard" error={Boolean(error[CODE])}>
        <InputLabel htmlFor="TextField-1" shrink={false}>
          {t(`table.code_mkb`)}
        </InputLabel>
        <TextField
          id="TextField-1"
          autoFocus
          name={CODE}
          value={data[CODE]}
          onChange={handleCodeChange}
          onBlur={() => handleValidate(data)}
          error={Boolean(error[CODE])}
          helperText={error[CODE] ?? null}
        />
      </FormControl>

      <FormControl
        fullWidth
        variant="standard"
        sx={{ mt: '2.5rem' }}
        error={Boolean(error[NAME_RU])}
      >
        <InputLabel htmlFor="TextField-2" shrink={false}>
          {t('table.name_ru')}
        </InputLabel>
        <TextField
          id="TextField-2"
          name={NAME_RU}
          value={codeLoading ? '...' : data[NAME_RU]}
          onChange={handleChange}
          onBlur={e => {
            handleChangeCase(e);
            handleValidate(data);
          }}
          error={Boolean(error[NAME_RU])}
          helperText={error[NAME_RU] ?? null}
          disabled={isCodeExists}
        />
      </FormControl>

      <FormControl
        fullWidth
        variant="standard"
        sx={{ mt: '2.5rem' }}
        error={Boolean(error[NAME_EN])}
      >
        <InputLabel htmlFor="TextField-3" shrink={false}>
          {t('table.name_en')}
        </InputLabel>
        <TextField
          id="TextField-3"
          name={NAME_EN}
          value={codeLoading ? '...' : data[NAME_EN]}
          onChange={handleChange}
          onBlur={e => {
            handleChangeCase(e);
            handleValidate(data);
          }}
          error={Boolean(error[NAME_EN])}
          helperText={error[NAME_EN] ?? null}
          disabled={isCodeExists}
        />
      </FormControl>

      <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
        <InputLabel id="Select-1-label" shrink={false}>
          {t('table.profile')}
        </InputLabel>
        <Select
          labelId="Select-1-label"
          id="Select-1"
          multiple
          name={PROFILE}
          value={data[PROFILE]}
          label={t('table.profile')}
          input={<OutlinedInput label={t('table.profile')} />}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{ autoFocus: false }}
          onChange={handleChange}
          renderValue={selected => selected.map(el => el).join('; ')}
        >
          {profiles.map(el => (
            <MenuItem
              key={el.id}
              value={el.profile_name}
              disabled={el.technical}
            >
              <Checkbox checked={data.profile.indexOf(el.profile_name) > -1} />
              <ListItemText primary={el.profile_name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', mt: '2rem', justifyContent: 'center' }}>
        <Button
          type="button"
          color="secondary"
          sx={{ mr: 1 }}
          variant="contained"
          onClick={onCancel}
        >
          {t('promptbar:cancel')}
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={
            isDisabledSaveBtn || Boolean(Object.values(error).some(el => el))
          }
          onClick={handleFormSubmit}
        >
          {t('promptbar:save')}
        </Button>
      </Box>
    </Box>
  );
};

AddICDCodeForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.object).isRequired,
  profiles: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default AddICDCodeForm;
